<template>
  <div
    class="mega-wrap"
    @mouseenter="openMenu"
    @mouseleave="scheduleClose"
  >
    <router-link
      :to="to"
      class="nav-link mega-trigger"
      :class="{ active, open: isOpen }"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      @focus="openMenu"
    >
      {{ label }}
      <svg class="mega-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </router-link>

    <transition name="mega">
      <div
        v-if="isOpen"
        class="mega-panel"
        role="menu"
        :aria-label="label"
        @mouseenter="openMenu"
      >
        <div class="mega-groups" role="list">
          <router-link
            v-for="group in groups"
            :key="group.id"
            :to="group.to"
            class="mega-group"
            :class="{ active: hoveredGroupId === group.id }"
            role="menuitem"
            @mouseenter="hoveredGroupId = group.id"
            @focus="hoveredGroupId = group.id"
            @click="closeNow"
          >
            <span class="mega-group-icon" :class="`mega-group-icon--${group.id}`" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 4h3a2 2 0 1 1 2 2v2h5a1 1 0 0 1 1 1v3a2 2 0 1 1-2 2h-2v5a1 1 0 0 1-1 1h-3a2 2 0 1 1-2-2V15H4a1 1 0 0 1-1-1v-3a2 2 0 1 1 2-2h3V6a2 2 0 0 1 0-2z" />
              </svg>
            </span>
            <span class="mega-group-copy">
              <strong>{{ group.title }}</strong>
              <small>{{ group.description }}</small>
            </span>
            <svg class="mega-group-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </router-link>
        </div>

        <div class="mega-popular" aria-live="polite">
          <div v-if="popularLoading" class="mega-popular-state">{{ t('servicesMenu.loading') }}</div>
          <div v-else-if="!popularItems.length" class="mega-popular-state">{{ t('servicesMenu.empty') }}</div>
          <div v-else class="mega-popular-grid">
            <router-link
              v-for="item in popularItems"
              :key="item.key"
              :to="item.to"
              class="mega-popular-item"
              @click="closeNow"
            >
              <img
                v-if="item.iconUrl && !brokenIcons.has(item.key)"
                :src="item.iconUrl"
                :alt="item.name"
                class="mega-popular-logo"
                @error="markBrokenIcon(item.key)"
              />
              <span v-else class="mega-popular-letter" aria-hidden="true">{{ item.letter }}</span>
              <span class="mega-popular-name">{{ item.name }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '@/composables/useI18n'
import alfabitPaymentsApi, { type ServiceBrief, type ServiceCategory } from '@/api/alfabitPayments'

interface MegaGroup {
  id: 'digital'
  title: string
  description: string
  to: { path: string; query?: Record<string, string> }
  categories: ServiceCategory[]
}

interface PopularItem {
  key: string
  name: string
  letter: string
  iconUrl: string | null
  to: { path: string; query: Record<string, string> }
}

const POPULAR_BRANDS = [
  'steam',
  'genshin',
  'apex',
  'roblox',
  'minecraft',
  'pubg',
  'warzone',
  'blizzard',
  'call of duty',
  'free fire',
  'mobile legends',
]

const GROUP_MATCHERS: Record<MegaGroup['id'], { aliases: string[]; names: RegExp }> = {
  digital: {
    aliases: ['internet_igry', 'onlayn_servisy'],
    names: /игр|онлайн сервис|цифр|ваучер|game|digital|online/i,
  },
}

const props = defineProps<{
  to: string
  label: string
  active: boolean
}>()

const { t } = useI18n()

const isOpen = ref(false)
const hoveredGroupId = ref<MegaGroup['id']>('digital')
const categories = ref<ServiceCategory[]>([])
const popularByGroup = ref<Record<string, PopularItem[]>>({})
const popularLoading = ref(false)
const catalogLoaded = ref(false)
const brokenIcons = ref(new Set<string>())

let closeTimer: ReturnType<typeof setTimeout> | null = null
let loadSeq = 0

const groups = computed<MegaGroup[]>(() => {
  const roots = categories.value.filter((c) => (c.parent_id ?? null) === null)
  return (['digital'] as const).map((id) => {
    const matched = pickGroupCategories(id, roots)
    return {
      id,
      title: t('servicesMenu.payServices'),
      description: t('servicesMenu.payServicesDesc'),
      to: matched[0]
        ? { path: props.to, query: { category: matched[0].alias } }
        : { path: props.to },
      categories: matched,
    }
  })
})

const popularItems = computed(() => popularByGroup.value[hoveredGroupId.value] || [])

function markBrokenIcon(key: string) {
  const next = new Set(brokenIcons.value)
  next.add(key)
  brokenIcons.value = next
}

function itemLetter(name: string) {
  const match = name.replace(/^["«„“']+\s*/u, '').match(/[\p{L}\p{N}]/u)
  return (match?.[0] || '?').toUpperCase()
}

function matchesGroup(id: MegaGroup['id'], cat: ServiceCategory) {
  const matcher = GROUP_MATCHERS[id]
  const alias = (cat.alias || '').toLowerCase()
  return matcher.aliases.includes(alias) || matcher.names.test(cat.name || '')
}

function pickGroupCategories(id: MegaGroup['id'], roots: ServiceCategory[]) {
  return roots.filter((cat) => matchesGroup(id, cat))
}

function childrenOf(cat: ServiceCategory) {
  return categories.value.filter((child) => child.parent_id === cat.id)
}

function brandScore(name: string) {
  const lower = name.toLowerCase()
  const index = POPULAR_BRANDS.findIndex((brand) => lower.includes(brand))
  return index === -1 ? 100 + name.length : index
}

function toPopularFromCategory(cat: ServiceCategory): PopularItem {
  return {
    key: `cat-${cat.id}`,
    name: cat.name,
    letter: itemLetter(cat.name),
    iconUrl: cat.icon_url || null,
    to: { path: props.to, query: { category: cat.alias } },
  }
}

function toPopularFromService(svc: ServiceBrief): PopularItem {
  return {
    key: `svc-${svc.id}`,
    name: svc.name,
    letter: itemLetter(svc.name),
    iconUrl: svc.icon_url,
    to: { path: props.to, query: { service: String(svc.id) } },
  }
}

async function loadCatalog() {
  if (catalogLoaded.value) return
  try {
    const { data } = await alfabitPaymentsApi.getCategories()
    categories.value = data.categories || []
    catalogLoaded.value = true
  } catch (error) {
    console.error('Failed to load services menu catalog:', error)
    categories.value = []
  }
}

async function loadPopular(groupId: MegaGroup['id']) {
  if (popularByGroup.value[groupId]) return
  const group = groups.value.find((item) => item.id === groupId)
  if (!group) return

  const seq = ++loadSeq
  popularLoading.value = true
  try {
    const brandItems = group.categories
      .flatMap((cat) => childrenOf(cat))
      .sort((a, b) => brandScore(a.name) - brandScore(b.name) || a.name.localeCompare(b.name, 'ru'))
      .slice(0, 8)
      .map(toPopularFromCategory)

    if (brandItems.length) {
      popularByGroup.value = { ...popularByGroup.value, [groupId]: brandItems }
      return
    }

    const items: PopularItem[] = []
    for (const cat of group.categories) {
      const { data } = await alfabitPaymentsApi.getServices({ category: cat.alias, limit: 8 })
      items.push(...(data.items || []).map(toPopularFromService))
      if (items.length >= 8) break
    }
    popularByGroup.value = { ...popularByGroup.value, [groupId]: items.slice(0, 8) }
  } catch (error) {
    console.error('Failed to load popular services:', error)
    if (!popularByGroup.value[groupId]) {
      popularByGroup.value = { ...popularByGroup.value, [groupId]: [] }
    }
  } finally {
    if (seq === loadSeq) popularLoading.value = false
  }
}

async function openMenu() {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  isOpen.value = true
  await loadCatalog()
  await loadPopular(hoveredGroupId.value)
}

function scheduleClose() {
  if (closeTimer) clearTimeout(closeTimer)
  closeTimer = setTimeout(() => {
    isOpen.value = false
    closeTimer = null
  }, 140)
}

function closeNow() {
  if (closeTimer) clearTimeout(closeTimer)
  isOpen.value = false
}

watch(hoveredGroupId, (id) => {
  if (isOpen.value) loadPopular(id)
})

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeNow()
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  if (closeTimer) clearTimeout(closeTimer)
})
</script>

<style scoped>
.mega-wrap {
  position: relative;
}

.mega-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.mega-chevron {
  opacity: 0.7;
  transition: transform 0.18s ease;
}

.mega-trigger.open .mega-chevron,
.mega-trigger.active .mega-chevron {
  transform: rotate(180deg);
  opacity: 1;
}

.mega-panel {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  right: auto;
  z-index: 160;
  display: grid;
  grid-template-columns: minmax(200px, 230px) minmax(220px, 1fr);
  width: min(480px, calc(100vw - 2rem));
  background: var(--background-secondary, #14161a);
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

[data-theme="light"] .mega-panel {
  background: #fff;
  box-shadow: 0 18px 48px rgba(16, 24, 40, 0.12);
}

.mega-groups {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem;
}

.mega-group {
  display: grid;
  grid-template-columns: 34px 1fr 12px;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
}

.mega-group:hover,
.mega-group.active {
  background: var(--background-tertiary, rgba(255, 255, 255, 0.04));
}

.mega-group-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-primary);
}

.mega-group-icon svg {
  width: 16px;
  height: 16px;
}

.mega-group.active .mega-group-icon--digital {
  color: var(--color-brand, #54d786);
  background: rgba(84, 215, 134, 0.12);
  border-color: transparent;
}

.mega-group-copy {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.mega-group-copy strong {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-primary);
}

.mega-group-copy small {
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--text-tertiary);
}

.mega-group-arrow {
  color: var(--text-tertiary);
  opacity: 0;
}

.mega-group:hover .mega-group-arrow,
.mega-group.active .mega-group-arrow {
  opacity: 1;
}

.mega-popular {
  min-height: 180px;
  padding: 0.7rem 0.85rem;
  border-left: 1px solid var(--border-primary);
}

.mega-popular-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.2rem 0.7rem;
}

.mega-popular-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 36px;
  padding: 0.25rem 0.3rem;
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-primary);
}

.mega-popular-item:hover {
  background: var(--background-tertiary, rgba(255, 255, 255, 0.04));
}

.mega-popular-logo,
.mega-popular-letter {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.mega-popular-logo {
  object-fit: contain;
  filter: grayscale(1) brightness(1.4);
}

[data-theme="light"] .mega-popular-logo {
  filter: grayscale(1);
}

.mega-popular-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--background-tertiary, rgba(255, 255, 255, 0.06));
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 700;
}

.mega-popular-name {
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mega-popular-state {
  height: 100%;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  font-size: 0.8125rem;
}

.mega-enter-active,
.mega-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.mega-enter-from,
.mega-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
